import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { useSession } from '../../../app/useSession';
import { useTeamMember } from '../../../app/useTeamMember';
import { useToast } from '../../../components/Toast';
import { useProjects } from '../projects/projectsQueries';
import { TaskDrawer } from './TaskDrawer';
import { TaskList } from './TaskList';
import type { TaskFilters, TaskRow, TaskStatus } from './tasks.schema';
import { useQuickAddTask, useTasks, useUpdateTaskStatus } from './tasksQueries';
import styles from './tasks.module.css';

const DEFAULT_FILTERS: TaskFilters = {
  projectId: 'all',
  showCompleted: false,
};

export function TasksPage() {
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const userId = sessionObj?.user.id ?? null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  // `?project=<id>` arrives from a project or customer page.
  const [filters, setFilters] = useState<TaskFilters>(() => ({
    ...DEFAULT_FILTERS,
    projectId: params.get('project') ?? DEFAULT_FILTERS.projectId,
  }));
  const { data: tasks = [], isLoading, error } = useTasks(orgId, filters);
  const { data: projects = [] } = useProjects(orgId);
  const quickAdd = useQuickAddTask(orgId, userId);
  const updateStatus = useUpdateTaskStatus(orgId);
  const [quickAddText, setQuickAddText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskRow | null>(null);

  const projectOptions = useMemo(() => projects.map((p) => ({ id: p.id, name: p.name })), [projects]);
  const projectNameById = useMemo(
    () =>
      projects.reduce<Record<string, string>>((acc, project) => {
        acc[project.id] = project.name;
        return acc;
      }, {}),
    [projects],
  );

  // `?new=1` opens the create drawer (Ctrl+K "צור משימה").
  const wantsNew = params.get('new') === '1';
  useEffect(() => {
    if (!wantsNew) return;
    setSelectedTask(null);
    setDrawerOpen(true);
    const next = new URLSearchParams(params);
    next.delete('new');
    setParams(next, { replace: true });
  }, [wantsNew, params, setParams]);

  function openCreate() {
    setSelectedTask(null);
    setDrawerOpen(true);
  }

  function openEdit(task: TaskRow) {
    setSelectedTask(task);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setSelectedTask(null);
  }

  async function handleQuickAdd(event: React.FormEvent) {
    event.preventDefault();
    const title = quickAddText.trim();
    if (!title) return;

    const filterIsSpecificProject =
      filters.projectId !== 'all' && filters.projectId !== 'none' && filters.projectId !== '';
    const projectId = filterIsSpecificProject ? filters.projectId : null;

    try {
      await quickAdd.mutateAsync({ title, project_id: projectId });
      setQuickAddText('');
    } catch {
      toast.error('לא הצלחנו להוסיף · נסה שוב');
    }
  }

  function handleStatusChange(id: string, status: TaskStatus) {
    updateStatus.mutate({ id, status });
  }

  return (
    <>
      <div className={styles.pageHead}>
        <div>
          <h1>משימות</h1>
          <p className={styles.pageSub}>מה צריך לעשות היום ובשבוע הקרוב.</p>
        </div>
        <button type="button" className={styles.btnPrimary} onClick={openCreate}>
          <Plus size={16} aria-hidden="true" />
          משימה
        </button>
      </div>

      <div className={styles.filterBar}>
        <label className={styles.filterField}>
          <span>פרויקט</span>
          <select
            className={styles.input}
            value={filters.projectId}
            onChange={(event) => {
              setFilters((current) => ({ ...current, projectId: event.target.value }));
            }}
          >
            <option value="all">הכל</option>
            <option value="none">אישיות (ללא פרויקט)</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>

        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={filters.showCompleted}
            onChange={(event) => {
              setFilters((current) => ({ ...current, showCompleted: event.target.checked }));
            }}
          />
          <span>הצג בוצעו/בוטלו</span>
        </label>
      </div>

      <form
        className={styles.quickAdd}
        onSubmit={(e) => {
          void handleQuickAdd(e);
        }}
      >
        <input
          type="text"
          className={styles.input}
          placeholder="הקלד משימה ולחץ Enter…"
          value={quickAddText}
          onChange={(event) => {
            setQuickAddText(event.target.value);
          }}
          disabled={quickAdd.isPending}
          aria-label="הוספה מהירה"
        />
        <button type="submit" className={styles.btnSecondary} disabled={quickAdd.isPending || !quickAddText.trim()}>
          הוסף
        </button>
      </form>

      {isLoading ? <p className={styles.notice}>טוען משימות...</p> : null}
      {error ? <p className={styles.notice}>לא הצלחנו לטעון משימות · נסה שוב</p> : null}

      {!isLoading && !error && tasks.length === 0 ? (
        <div className={styles.emptyCard}>
          <p>אין משימות עכשיו · התחל עם הקלדה מהירה למעלה</p>
        </div>
      ) : null}

      {!isLoading && !error && tasks.length > 0 ? (
        <TaskList
          tasks={tasks}
          projectNameById={projectNameById}
          onSelect={openEdit}
          onChangeStatus={handleStatusChange}
        />
      ) : null}

      <TaskDrawer
        open={drawerOpen}
        task={selectedTask}
        projects={projectOptions}
        orgId={orgId}
        userId={userId}
        onClose={closeDrawer}
      />
    </>
  );
}
